import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrainIcon, ChevronDownIcon, ChevronUpIcon, DownloadIcon, InfoIcon, UserPlusIcon } from 'lucide-react';
import { AddPlayersModal } from './AddPlayersModal';
import type { NeuroPreviewState, PlayerCri, SortKey, TeamCode, TeamFilter } from '../../types/neuro';
import { defaultVisibleIds, neuroPlayers, neuroTeams, teamOrder } from '../../data/neuroPlayers';
import { sortPlayers, toneClasses } from '../../utils/neuro';
import { InfoTooltip } from './InfoTooltip';
import { NeuroSkeleton } from './NeuroSkeleton';
import { NeuroToast } from './NeuroToast';
import { PlayerCriCard } from './PlayerCriCard';
import { PlayerCriDetail } from './PlayerCriDetail';
import { ToneChip } from './NeuroPrimitives';
const TEAM_FILTERS: Array<{
  id: TeamFilter;
  label: string;
}> = [{
  id: 'both',
  label: 'Both Teams'
}, {
  id: 'NYK',
  label: 'New York Knicks'
}, {
  id: 'LAL',
  label: 'Los Angeles Lakers'
}];
const SORT_OPTIONS: Array<{
  id: SortKey;
  label: string;
}> = [{
  id: 'cri',
  label: 'CRI'
}, {
  id: 'minutes',
  label: 'Minutes'
}, {
  id: 'focus',
  label: 'Focus'
}, {
  id: 'impulsivity',
  label: 'Impulsivity (lowest first)'
}, {
  id: 'reaction',
  label: 'Reaction'
}];
const PREVIEW_OPTIONS: Array<{
  id: NeuroPreviewState;
  label: string;
}> = [{
  id: 'default',
  label: 'Default'
}, {
  id: 'loading',
  label: 'Loading'
}, {
  id: 'empty',
  label: 'Empty'
}, {
  id: 'oneTeamMissing',
  label: 'One team missing'
}];
export const NeuroTab = () => {
  const [preview, setPreview] = useState<NeuroPreviewState>('default');
  const [teamFilter, setTeamFilter] = useState<TeamFilter>('both');
  const [sortKey, setSortKey] = useState<SortKey>('cri');
  const [selected, setSelected] = useState<PlayerCri | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [visibleIds, setVisibleIds] = useState<string[]>(defaultVisibleIds);
  const [showAll, setShowAll] = useState<Record<TeamCode, boolean>>({
    LAL: false,
    NYK: false
  });
  const [modalTeam, setModalTeam] = useState<TeamCode | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const closeModal = useCallback(() => setModalTeam(null), []);
  const addPlayers = (ids: string[]) => {
    setVisibleIds(prev => [...prev, ...ids.filter(id => !prev.includes(id))]);
    setModalTeam(null);
  };
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    setSelected(null);
  }, [preview]);
  const players: PlayerCri[] = preview === 'empty' ? [] : preview === 'oneTeamMissing' ? neuroPlayers.filter(p => p.team === 'LAL') : neuroPlayers;
  const isLoading = preview === 'loading';
  const isEmpty = !isLoading && players.length === 0;
  const visibleTeams: TeamCode[] = teamFilter === 'both' ? teamOrder : [teamFilter];
  const openPlayer = (player: PlayerCri) => {
    setSelected(player);
    cardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const download = () => setToast('Preparing PDF…');
  return <div className="neuro-font" data-id="element-531" data-mp="qf5yxhz5mpwf">
      <div ref={cardRef} className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm scroll-mt-4" data-id="element-532" data-mp="bl14r1owfhlv">
        {selected ? <PlayerCriDetail player={selected} onBack={() => setSelected(null)} onDownload={download} data-id="element-533" data-mp="gl5p52sxgwhd" /> : <>
            {/* Card header */}
            <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-534" data-mp="3oz5xfxwzzfob">
              <div data-id="element-535" data-mp="3ox1jso9ar2au">
                <h2 className="text-xl font-bold text-[#111827]" data-id="element-536" data-mp="3ty8sqqn6e7j3">
                  Cognitive Readiness
                </h2>
                <p className="mt-1 text-sm text-[#6B7280]" data-id="element-537" data-mp="3fy7qp7v6cu21">
                  How mentally ready each player was during this game
                </p>
              </div>
              {!isEmpty && <button type="button" onClick={download} disabled={isLoading} className="inline-flex items-center gap-2 rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-medium text-[#374151] hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 whitespace-nowrap" data-id="element-538" data-mp="3cx4lpda8vshl">
                  <DownloadIcon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" data-id="element-539" data-mp="2p7jz8kevud3k" />
                  Download match report
                </button>}
            </div>

            {isEmpty ? <div className="py-20 flex flex-col items-center text-center" data-id="element-540" data-mp="2f9w6g00u7fo1">
                <span className="w-12 h-12 rounded-full bg-[#F3F4F6] flex items-center justify-center" data-id="element-541" data-mp="igkkzx07j93z">
                  <BrainIcon className="w-6 h-6 text-[#6B7280]" strokeWidth={1.75} aria-hidden="true" data-id="element-542" data-mp="dhzl5yovarg2" />
                </span>
                <p className="mt-4 font-semibold text-[#111827]" data-id="element-543" data-mp="lxp4n2ldyvbo">
                  Cognitive analysis not available for this match yet
                </p>
                <p className="mt-1 text-sm text-[#6B7280]" data-id="element-544" data-mp="ur24kohvax79">
                  Neuro data is processed for selected matches only.
                </p>
              </div> : <>
                {/* Toolbar */}
                <div className="mt-6 flex flex-wrap items-center justify-between gap-4" data-id="element-545" data-mp="30nsmxtezuy8">
                  <div className="flex bg-gray-100 rounded-lg p-1 w-fit" role="group" aria-label="Filter by team" data-id="element-546" data-mp="70pnan0qfo40">
                    {TEAM_FILTERS.map(filter => <button key={filter.id} type="button" aria-pressed={teamFilter === filter.id} onClick={() => setTeamFilter(filter.id)} className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] ${teamFilter === filter.id ? 'bg-white shadow text-[#111827]' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-547" data-mp="gxkkn2l9rigp">
                        {filter.label}
                      </button>)}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-[#6B7280]" data-id="element-548" data-mp="f2njxts7bxu3">
                    Sort by
                    <select value={sortKey} onChange={e => setSortKey(e.target.value as SortKey)} className="rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 pr-8 text-sm font-medium text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#2563EB]" data-id="element-549" data-mp="trbf3yy4y5ji">
                      {SORT_OPTIONS.map(option => <option key={option.id} value={option.id} data-id="element-550" data-mp="27vv91x6tfvv7">
                          {option.label}
                        </option>)}
                    </select>
                  </label>
                </div>

                {/* Caption + legend */}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-y border-[#E5E7EB] py-3" data-id="element-551" data-mp="22zek50rsldh2">
                  <p className="flex items-center gap-1.5 text-xs text-[#6B7280]" data-id="element-552" data-mp="2qmmsxsofj4nu">
                    Top 4 players shown per team. Players with too few in-game
                    events are not analyzed.
                    <InfoTooltip label="minimum sample" align="left" text="The model needs at least 12 tracked in-game events (shots, passes, turnovers, fouls, rebounds) to compute a player's indices. Players below that threshold are left out to avoid misleading numbers." data-id="element-553" data-mp="2lpcs7gvlb6kp" />
                  </p>
                  <div className="flex flex-wrap items-center gap-2" data-id="element-554" data-mp="21vo9oimszxgu">
                    <ToneChip tone="ready" data-id="element-555" data-mp="1wx4ju753a1j7">
                      <span className={`w-1.5 h-1.5 rounded-full ${toneClasses.ready.dot}`} aria-hidden="true" data-id="element-556" data-mp="1sx2u8wcmoyka" />
                      70+ Ready
                    </ToneChip>
                    <ToneChip tone="moderate" data-id="element-557" data-mp="1o02n0i41lprl">
                      <span className={`w-1.5 h-1.5 rounded-full ${toneClasses.moderate.dot}`} aria-hidden="true" data-id="element-558" data-mp="2koibeavf1t34" />
                      50 to 69 Moderate
                    </ToneChip>
                    <ToneChip tone="low" data-id="element-559" data-mp="2bvorg050y5ix">
                      <span className={`w-1.5 h-1.5 rounded-full ${toneClasses.low.dot}`} aria-hidden="true" data-id="element-560" data-mp="2budpm5v537ug" />
                      Below 50 Low
                    </ToneChip>
                    <span className="ml-1 text-xs text-[#6B7280]" data-id="element-561" data-mp="34dayr3bmejyl">
                      Impulsivity: lower is better
                    </span>
                  </div>
                </div>

                {/* Player grid */}
                <div className="mt-6" data-id="element-562" data-mp="36y8ndqsamnqn">
                  {isLoading ? <NeuroSkeleton data-id="element-563" data-mp="3bwtnzrbgj9fm" /> : <div className="space-y-8" data-id="element-564" data-mp="3guv7uegpjcs5">
                      {visibleTeams.map(code => {
                const team = neuroTeams[code];
                const allTeamPlayers = players.filter(p => p.team === code);
                const expanded = showAll[code];
                const teamPlayers = sortPlayers(expanded ? allTeamPlayers : allTeamPlayers.filter(p => visibleIds.includes(p.player_id)), sortKey);
                const hiddenCount = allTeamPlayers.length - teamPlayers.length;
                return <section key={code} aria-labelledby={`neuro-team-${code}`} data-id="element-565" data-mp="hdngweq9mgld">
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-3" data-id="element-566" data-mp="1l8exa07j1xmv">
                              <div className="flex items-center gap-2" data-id="element-567" data-mp="1q5ybj0486aet">
                                <img src={team.logo} alt="" className="w-6 h-6 object-contain" data-id="element-568" data-mp="1v3qbmhf0x2u3" />
                                <h3 id={`neuro-team-${code}`} className={`text-sm font-semibold ${team.textClass}`} data-id="element-569" data-mp="23wtjma6wgnhc">
                                  {team.name}
                                </h3>
                                {allTeamPlayers.length > 0 && <span className="text-xs text-[#6B7280]" data-id="element-570" data-mp="ji9t6ehzksgx">
                                    {teamPlayers.length} of {allTeamPlayers.length} players
                                  </span>}
                              </div>
                              {allTeamPlayers.length > 0 && <div className="flex items-center gap-2" data-id="element-571" data-mp="2wxrt5qisgek1">
                                  <button type="button" aria-expanded={expanded} onClick={() => setShowAll(prev => ({
                        ...prev,
                        [code]: !prev[code]
                      }))} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-[#374151] hover:bg-[#F3F4F6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] whitespace-nowrap" data-id="element-572" data-mp="2sysma0dhy7py">
                                    {expanded ? <ChevronUpIcon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" data-id="element-573" data-mp="1va91oiuzwf36" /> : <ChevronDownIcon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" data-id="element-574" data-mp="1wpv2sdwkgcn8" />}
                                    {expanded ? 'Show less' : 'Show all'}
                                  </button>
                                  <button type="button" onClick={() => setModalTeam(code)} disabled={hiddenCount === 0} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D1D5DB] bg-white px-2.5 py-1.5 text-sm font-medium text-[#374151] hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 whitespace-nowrap" data-id="element-575" data-mp="1soja1ziebbat">
                                    <UserPlusIcon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" data-id="element-576" data-mp="103hwmj03sy5v" />
                                    Add player
                                  </button>
                                </div>}
                            </div>
                            {teamPlayers.length === 0 ? <div className="flex items-center gap-2 rounded-xl border border-dashed border-[#D1D5DB] bg-[#F9FAFB] px-4 py-5 text-sm text-[#6B7280]" data-id="element-577" data-mp="ldonh49max1f">
                                <InfoIcon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" data-id="element-578" data-mp="ufo59ysgi55f" />
                                Neuro data for {team.name} is not available for this match.
                              </div> : <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 auto-rows-fr" data-id="element-579" data-mp="147areq6c1omy">
                                {teamPlayers.map(player => <PlayerCriCard key={player.player_id} player={player} onOpen={openPlayer} data-id="element-580" data-mp="1e4f31w2jy1pi" />)}
                              </div>}
                          </section>;
              })}
                    </div>}
                </div>
              </>}
          </>}
      </div>

      {/* Prototype-only preview switcher (not part of the product UI) */}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3 rounded-lg border border-dashed border-[#D1D5DB] px-4 py-3" data-id="element-581" data-mp="2tofr1e2bor5l">
        <span className="text-xs font-medium text-[#6B7280]" data-id="element-582" data-mp="32nj9qgxkv4jk">Prototype preview</span>
        <div className="flex bg-gray-100 rounded-lg p-1" role="group" aria-label="Preview state" data-id="element-583" data-mp="dv7c0si6l85t">
          {PREVIEW_OPTIONS.map(option => <button key={option.id} type="button" aria-pressed={preview === option.id} onClick={() => setPreview(option.id)} className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] ${preview === option.id ? 'bg-white shadow text-[#111827]' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-584" data-mp="hvhfnyii3mhi">
              {option.label}
            </button>)}
        </div>
      </div>

      {modalTeam && <AddPlayersModal team={modalTeam} candidates={sortPlayers(players.filter(p => p.team === modalTeam && !visibleIds.includes(p.player_id)), 'cri')} onClose={closeModal} onConfirm={addPlayers} data-id="element-585" data-mp="1erd7au3y1t8t" />}

      <NeuroToast message={toast} data-id="element-586" data-mp="302v1qqg27vkj" />
    </div>;
};
export default NeuroTab;